import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import {
  getBillingCycleRevenueEstimateApiBillingBillingCycleBillingCycleIdRevenueEstimateGet,
  getEpisodeHhrgCostsBillingWorksheetEpisodeEpisodeIdHhrgCostsGet,
  getEpisodeHhrgPeriodsApiBillingWorksheetEpisodeEpisodeIdPeriodsGet,
} from '../../services/api/sdk.gen';
import type {
  EpisodeHhrgCostResponse,
  EpisodeHhrgPeriodResponse,
  EpisodeSummaryResponse,
  QualityReportingStatus,
  RevenueEstimate,
  RevenueEstimateStatus,
} from '../../services/api/types.gen';
import { formatDateToISO, parseISOToLocalDate } from '../../utils/dateUtils';
import {
  combineHhrgWorksheet,
  type HhrgDisciplineCostInput,
  type HhrgPeriodInput,
  type HhrgRevenueUnavailableReason,
  type HhrgWorksheetOutput,
} from '../../utils/hhrgWorksheetCombination';
import HhrgWorksheetPresentation from './HhrgWorksheetPresentation';

export type HhrgWorksheetTabProps = {
  episode: Pick<EpisodeSummaryResponse, 'id' | 'start_date' | 'end_date'>;
  patientName?: string;
  /** The estimate API requires an explicit QRP rate input. */
  qualityReportingStatus?: QualityReportingStatus | null;
};

const PAYMENT_PATH_QUERY_PARAM = 'payment_path';
const QUALITY_REPORTING_STATUS_QUERY_PARAM = 'quality_reporting_status';
type SupportedPaymentPath = 'NORMAL_NON_LUPA';

type HhrgCostData = ReadonlyMap<string, EpisodeHhrgCostResponse>;
export type HhrgRevenueData = ReadonlyMap<string, RevenueEstimate | undefined>;

type HhrgPeriods = readonly EpisodeHhrgPeriodResponse[];

const parsePaymentPath = (value: string | null): SupportedPaymentPath | null =>
  value === 'NORMAL_NON_LUPA' ? value : null;

const parseQualityReportingStatus = (
  value: string | null
): QualityReportingStatus | null =>
  value === 'QRP_SUBMITTER' || value === 'QRP_NON_SUBMITTER' ? value : null;

const addDays = (dateString: string, days: number): string => {
  const date = parseISOToLocalDate(dateString);
  if (!date) return dateString;
  date.setDate(date.getDate() + days);
  return formatDateToISO(date);
};

const periodKey = (startDate: string, endDate: string): string =>
  `${startDate}:${endDate}`;

const expectedEpisodePeriods = (
  episode: HhrgWorksheetTabProps['episode']
): readonly [
  { start_date: string; end_date: string },
  { start_date: string; end_date: string },
] => {
  const firstEndDate = addDays(episode.start_date, 29);
  const secondStartDate = addDays(firstEndDate, 1);
  return [
    { start_date: episode.start_date, end_date: firstEndDate },
    { start_date: secondStartDate, end_date: addDays(secondStartDate, 29) },
  ];
};

const isValidEpisodePeriods = (
  episode: HhrgWorksheetTabProps['episode'],
  candidate: unknown
): candidate is HhrgPeriods => {
  if (!Array.isArray(candidate) || candidate.length !== 2) return false;

  const expected = expectedEpisodePeriods(episode);
  const seenCycleIds = new Set<string>();
  return candidate.every((period, index) => {
    if (
      period === null ||
      typeof period !== 'object' ||
      !('start_date' in period) ||
      !('end_date' in period) ||
      !('billing_cycle_id' in period)
    ) {
      return false;
    }
    const value = period as EpisodeHhrgPeriodResponse;
    if (
      value.start_date !== expected[index].start_date ||
      value.end_date !== expected[index].end_date ||
      (value.billing_cycle_id !== null &&
        typeof value.billing_cycle_id !== 'string')
    ) {
      return false;
    }
    if (value.billing_cycle_id !== null) {
      if (seenCycleIds.has(value.billing_cycle_id)) return false;
      seenCycleIds.add(value.billing_cycle_id);
    }
    return true;
  });
};

const costsForPeriod = (
  period: EpisodeHhrgPeriodResponse,
  costData: HhrgCostData | undefined
): HhrgDisciplineCostInput[] => {
  const response = costData?.get(periodKey(period.start_date, period.end_date));
  if (!response) return [];

  return response.rows.map(row => {
    const parsedCost =
      row.cost_per_visit === null || row.cost_per_visit === undefined
        ? null
        : Number(row.cost_per_visit);
    return {
      discipline: row.discipline,
      estimatedVisits: row.estimated_visits,
      perVisitCost:
        parsedCost !== null && Number.isFinite(parsedCost) && parsedCost >= 0
          ? parsedCost
          : null,
    };
  });
};

const revenueUnavailableReason = (
  status: RevenueEstimateStatus
): HhrgRevenueUnavailableReason => {
  switch (status) {
    case 'LUPA':
      return 'lupa';
    case 'PEP':
      return 'pep';
    case 'OUTLIER':
      return 'outlier';
    case 'LATE_NOA_OR_FILING_PENALTY':
      return 'late_noa';
    case 'MISSING_INPUT':
      return 'missing_input';
    case 'NORMAL_NON_LUPA':
      return 'unsupported';
    default:
      return 'unsupported';
  }
};

// eslint-disable-next-line react-refresh/only-export-components -- exported for unit testing
export const adaptRevenueEstimate = (
  estimate: RevenueEstimate | undefined
): {
  display: Partial<HhrgPeriodInput['display']>;
  revenue: HhrgPeriodInput['revenue'];
} => {
  const display = {
    oasisSource: estimate?.oasis_source ?? null,
    oasisDate: estimate?.oasis_date ?? null,
    primaryDiagnosis: estimate?.primary_diagnosis ?? null,
    admissionSource: estimate?.admission_source ?? null,
    admissionTiming: estimate?.admission_timing ?? null,
    clinicalGroup: estimate?.clinical_group ?? null,
    clinicalLevel: estimate?.clinical_level ?? null,
    comorbidityAdjustment: estimate?.comorbidity_adjustment ?? null,
    paymentStatus: estimate?.status ?? null,
    hippsCode: estimate?.hipps_code ?? null,
    coveredVisitCount: estimate?.covered_visit_count ?? null,
    lupaThreshold: estimate?.lupa_threshold ?? null,
    caseMixWeight: estimate?.case_mix_weight ?? null,
    nationalRate: estimate?.national_rate ?? null,
    wageIndex: estimate?.wage_index ?? null,
    wageIndexAreaCode: estimate?.wage_index_area_code ?? null,
    qualityReportingStatus: estimate?.quality_reporting_status ?? null,
    lupaStatus:
      estimate?.status === 'LUPA'
        ? 'LUPA'
        : estimate?.status === 'NORMAL_NON_LUPA'
          ? 'Not LUPA'
          : null,
  };

  // Worksheet revenue is meaningful only for a normal, non-LUPA payment with a calculated amount.
  if (
    estimate?.status !== 'NORMAL_NON_LUPA' ||
    estimate.amount === null ||
    estimate.amount === undefined
  ) {
    return {
      display,
      revenue: {
        status: 'unavailable',
        reason: estimate
          ? revenueUnavailableReason(estimate.status)
          : 'request_failed',
      },
    };
  }

  const amount = Number(estimate.amount);
  if (!Number.isFinite(amount) || amount < 0) {
    return {
      display,
      revenue: { status: 'unavailable', reason: 'unsupported' },
    };
  }
  return { display, revenue: { status: 'available', value: amount } };
};

// eslint-disable-next-line react-refresh/only-export-components -- exported for unit testing
export const adaptEpisodeToHhrgWorksheet = (
  episode: HhrgWorksheetTabProps['episode'],
  periods: HhrgPeriods = [],
  costData?: HhrgCostData,
  revenueData?: HhrgRevenueData
): HhrgWorksheetOutput | null => {
  if (!isValidEpisodePeriods(episode, periods)) return null;

  const periodInputs = periods.map((period, index): HhrgPeriodInput => {
    const revenueEstimate = period.billing_cycle_id
      ? revenueData?.get(period.billing_cycle_id)
      : undefined;
    const revenue = period.billing_cycle_id
      ? revenueData
        ? adaptRevenueEstimate(revenueEstimate)
        : {
            display: {},
            revenue: { status: 'unavailable', reason: 'unsupported' } as const,
          }
      : {
          display: {},
          revenue: { status: 'unavailable', reason: 'unsupported' } as const,
        };
    return {
      display: {
        periodNumber: (index + 1) as 1 | 2,
        startDate: period.start_date,
        endDate: period.end_date,
        oasisSource: null,
        oasisDate: null,
        primaryDiagnosis: null,
        admissionSource: null,
        admissionTiming: null,
        clinicalGroup: null,
        clinicalLevel: null,
        comorbidityAdjustment: null,
        ...revenue.display,
      },
      billingCycleId: period.billing_cycle_id,
      revenue: revenue.revenue,
      costs: costsForPeriod(period, costData),
    };
  });

  return combineHhrgWorksheet(
    periodInputs as [HhrgPeriodInput, HhrgPeriodInput]
  );
};

export function HhrgWorksheetTab({
  episode,
  patientName,
  qualityReportingStatus,
}: HhrgWorksheetTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const paymentPath = parsePaymentPath(
    searchParams.get(PAYMENT_PATH_QUERY_PARAM)
  );
  const qualityReportingStatusUrlValue = searchParams.get(
    QUALITY_REPORTING_STATUS_QUERY_PARAM
  );
  const qualityReportingStatusFromUrl = parseQualityReportingStatus(
    qualityReportingStatusUrlValue
  );
  const [periods, setPeriods] = useState<HhrgPeriods | null>(null);
  const [periodsError, setPeriodsError] = useState(false);
  const [costData, setCostData] = useState<HhrgCostData | null>(null);
  const [costsError, setCostsError] = useState(false);
  const [revenueData, setRevenueData] = useState<HhrgRevenueData | null>(null);
  const [revenueError, setRevenueError] = useState(false);
  const [worksheetOverrides, setWorksheetOverrides] = useState<
    Partial<Record<1 | 2, readonly HhrgDisciplineCostInput[]>>
  >({});
  const [selectedQualityReportingStatus, setSelectedQualityReportingStatus] =
    useState<QualityReportingStatus | null>(() =>
      qualityReportingStatusUrlValue === null
        ? (qualityReportingStatus ?? null)
        : qualityReportingStatusFromUrl
    );
  const previousQualityReportingStatusUrlValue = useRef(
    qualityReportingStatusUrlValue
  );

  useEffect(() => {
    if (
      previousQualityReportingStatusUrlValue.current ===
      qualityReportingStatusUrlValue
    ) {
      return;
    }
    previousQualityReportingStatusUrlValue.current =
      qualityReportingStatusUrlValue;
    setSelectedQualityReportingStatus(qualityReportingStatusFromUrl);
  }, [qualityReportingStatusFromUrl, qualityReportingStatusUrlValue]);

  useEffect(() => {
    let active = true;
    setPeriods(null);
    setPeriodsError(false);
    setCostData(null);
    setCostsError(false);
    setRevenueData(null);
    setRevenueError(false);
    setWorksheetOverrides({});

    if (!episode.id) {
      setPeriodsError(true);
      return () => {
        active = false;
      };
    }

    void (async () => {
      try {
        const periodResponse =
          await getEpisodeHhrgPeriodsApiBillingWorksheetEpisodeEpisodeIdPeriodsGet(
            { path: { episode_id: episode.id } }
          );
        if (
          !periodResponse.data ||
          periodResponse.error ||
          !isValidEpisodePeriods(episode, periodResponse.data)
        ) {
          console.error(
            'Failed to load HHRG worksheet periods:',
            periodResponse.error ?? periodResponse
          );
          if (active) setPeriodsError(true);
          return;
        }
        if (!active) return;
        setPeriods(periodResponse.data);

        try {
          const costResponses = await Promise.all(
            periodResponse.data.map(async period => {
              const result =
                await getEpisodeHhrgCostsBillingWorksheetEpisodeEpisodeIdHhrgCostsGet(
                  {
                    path: { episode_id: episode.id },
                    query: {
                      period_start_date: period.start_date,
                      period_end_date: period.end_date,
                    },
                  }
                );
              return { period, result };
            })
          );
          if (!active) return;
          if (
            costResponses.some(
              ({ period, result }) =>
                result.error ||
                !result.data ||
                result.data.episode_id !== episode.id ||
                result.data.period_start_date !== period.start_date ||
                result.data.period_end_date !== period.end_date
            )
          ) {
            console.error('Failed to load HHRG cost inputs', {
              costResponses,
            });
            setCostsError(true);
            return;
          }
          setCostData(
            new Map(
              costResponses.map(({ period, result }) => [
                periodKey(period.start_date, period.end_date),
                result.data!,
              ])
            )
          );
        } catch (error) {
          console.error('Failed to load HHRG cost inputs:', error);
          if (active) setCostsError(true);
        }
      } catch (error) {
        console.error('Failed to load HHRG worksheet periods:', error);
        if (active) setPeriodsError(true);
      }
    })();

    return () => {
      active = false;
    };
  }, [episode.id, episode.start_date, episode.end_date]);

  useEffect(() => {
    let active = true;
    setRevenueData(null);
    setRevenueError(false);

    if (periods === null) return;
    if (paymentPath !== 'NORMAL_NON_LUPA') {
      setRevenueData(new Map());
      return;
    }

    const persistedPeriods = periods.filter(
      (
        period
      ): period is EpisodeHhrgPeriodResponse & { billing_cycle_id: string } =>
        period.billing_cycle_id !== null
    );
    if (persistedPeriods.length === 0) {
      setRevenueData(new Map());
      return;
    }

    void Promise.all(
      persistedPeriods.map(async period => {
        const result =
          await getBillingCycleRevenueEstimateApiBillingBillingCycleBillingCycleIdRevenueEstimateGet(
            {
              path: { billing_cycle_id: period.billing_cycle_id },
              query: {
                payment_path: paymentPath,
                quality_reporting_status: selectedQualityReportingStatus,
              },
            }
          );
        return { cycleId: period.billing_cycle_id, result };
      })
    )
      .then(results => {
        if (!active) return;
        if (results.some(({ result }) => result.error || !result.data)) {
          console.error('Failed to load HHRG revenue estimate', { results });
          setRevenueError(true);
          return;
        }
        setRevenueData(
          new Map(results.map(({ cycleId, result }) => [cycleId, result.data]))
        );
      })
      .catch(error => {
        console.error('Failed to load HHRG revenue estimate:', error);
        if (active) setRevenueError(true);
      });

    return () => {
      active = false;
    };
  }, [periods, paymentPath, selectedQualityReportingStatus]);

  const baseOutput = useMemo(
    () =>
      periods && costData && revenueData
        ? adaptEpisodeToHhrgWorksheet(episode, periods, costData, revenueData)
        : null,
    [costData, episode, periods, revenueData]
  );

  const output = useMemo(() => {
    if (!baseOutput) return null;
    return combineHhrgWorksheet(
      baseOutput.periods.map(period => ({
        ...period,
        costs:
          worksheetOverrides[period.display.periodNumber] ??
          period.costs.map(({ discipline, estimatedVisits, perVisitCost }) => ({
            discipline,
            estimatedVisits,
            perVisitCost,
          })),
      }))
    );
  }, [baseOutput, worksheetOverrides]);

  const handleCostsChange = (
    periodNumber: 1 | 2,
    costs: readonly HhrgDisciplineCostInput[]
  ) => {
    setWorksheetOverrides(previous => ({ ...previous, [periodNumber]: costs }));
  };

  const handleResetCosts = (periodNumber: 1 | 2) => {
    setWorksheetOverrides(previous => {
      const next = { ...previous };
      delete next[periodNumber];
      return next;
    });
  };

  return (
    <section data-testid='hhrg-worksheet-tab' className='space-y-4'>
      <div className='text-sm text-muted-foreground'>
        <p>Patient: {patientName?.trim() || '—'}</p>
        <p>
          Episode: {episode.start_date} – {episode.end_date}
        </p>
      </div>
      <div className='max-w-sm space-y-1'>
        <label
          htmlFor='hhrg-quality-reporting-status'
          className='text-sm font-medium text-foreground'
        >
          QRP reporting status
        </label>
        <Select
          value={selectedQualityReportingStatus ?? 'unset'}
          onValueChange={(value: string) => {
            const nextStatus =
              value === 'unset' ? null : parseQualityReportingStatus(value);
            setSelectedQualityReportingStatus(nextStatus);
            setSearchParams(
              current => {
                const next = new URLSearchParams(current);
                if (nextStatus === null) {
                  next.delete(QUALITY_REPORTING_STATUS_QUERY_PARAM);
                } else {
                  next.set(QUALITY_REPORTING_STATUS_QUERY_PARAM, nextStatus);
                }
                return next;
              },
              { replace: true }
            );
          }}
        >
          <SelectTrigger
            id='hhrg-quality-reporting-status'
            selectSize='sm'
            className='w-full'
          >
            <SelectValue placeholder='Select status' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='unset'>Select status</SelectItem>
            <SelectItem value='QRP_SUBMITTER'>QRP submitter</SelectItem>
            <SelectItem value='QRP_NON_SUBMITTER'>QRP non-submitter</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {periods === null && !periodsError ? (
        <p role='status' className='text-sm text-muted-foreground'>
          Loading worksheet periods...
        </p>
      ) : periodsError ? (
        <p role='status' className='text-sm text-muted-foreground'>
          Unable to load worksheet periods. HHRG worksheet is unavailable.
        </p>
      ) : costData === null && !costsError ? (
        <p role='status' className='text-sm text-muted-foreground'>
          Loading visit and cost details...
        </p>
      ) : costsError ? (
        <p role='status' className='text-sm text-muted-foreground'>
          Unable to load visit and cost details. HHRG worksheet is unavailable.
        </p>
      ) : revenueData === null && !revenueError ? (
        <p role='status' className='text-sm text-muted-foreground'>
          Loading revenue estimate...
        </p>
      ) : revenueError ? (
        <p role='status' className='text-sm text-muted-foreground'>
          Unable to load revenue details. HHRG worksheet is unavailable.
        </p>
      ) : output ? (
        <HhrgWorksheetPresentation
          output={output}
          onCostsChange={handleCostsChange}
          onResetCosts={handleResetCosts}
        />
      ) : (
        <p role='status' className='text-sm text-muted-foreground'>
          HHRG worksheet is unavailable.
        </p>
      )}
    </section>
  );
}

export default HhrgWorksheetTab;
